import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ESTADO_FLUJO } from "@/config/copiloto";
import { EstadoBadge, JurBadge } from "@/components/copiloto/EstadoBadge";
import { AccionesEstado } from "@/components/copiloto/AccionesEstado";
import { EntryDialog } from "@/components/copiloto/EntryDialog";

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-MX", { dateStyle: "medium" }) : "");

const EntryRow = ({ kind, e, meta, onEdit, onDone }) => (
  <li className="p-4 sm:p-5 hover:bg-[#FBF9F5]" data-testid={`entry-row-${e.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <EstadoBadge estado={e.estado} testid={`entry-estado-${e.id}`} />
      <JurBadge jur={e.jurisdiccion} />
      {kind === "why" && <span className="text-xs text-slate-500">{meta.documentos[e.document] || e.document} · {(e.fields || []).join(", ")}</span>}
      {kind === "faq" && e.categoria && <span className="text-xs text-slate-500">{e.categoria}</span>}
      {e.publicado && e.estado !== "publicado" && <span className="text-[10px] text-[#047857] font-semibold">Hay versión publicada en uso</span>}
    </div>
    <div className="mt-2 font-semibold text-slate-900">{e.title || e.pregunta}</div>
    <p className="mt-1 text-sm text-slate-600 line-clamp-2 whitespace-pre-line">{e.body || e.respuesta}</p>
    {e.revisado_por && (
      <p className="mt-2 text-xs text-slate-500" data-testid={`entry-revision-${e.id}`}>
        Revisó {e.revisado_por} · {fecha(e.fecha_revision)}{e.notas_revisor ? ` · “${e.notas_revisor}”` : ""}
      </p>
    )}
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Button size="sm" variant="outline" className="h-8" onClick={() => onEdit(e)} data-testid={`entry-edit-${e.id}`}>
        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
      </Button>
      <AccionesEstado kind={kind} entry={e} permisos={meta.permisos} onDone={onDone} />
    </div>
  </li>
);

export const EntriesPanel = ({ kind, meta }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState("todos");
  const [jur, setJur] = useState("todas");
  const [doc, setDoc] = useState("todos");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await api.get(`/copiloto/${kind}`)).data); }
    catch (e) { toast.error("No se pudo cargar el contenido"); }
    finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const upsert = (d, isNew) => setItems((p) => (isNew ? [...p, d] : p.map((x) => (x.id === d.id ? d : x))));
  const counts = useMemo(() => Object.fromEntries(Object.keys(ESTADO_FLUJO).map((s) => [s, items.filter((e) => e.estado === s).length])), [items]);
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items.filter((e) => (estado === "todos" || e.estado === estado)
      && (jur === "todas" || e.jurisdiccion === jur)
      && (doc === "todos" || e.document === doc)
      && (!qq || [e.id, e.title, e.body, e.pregunta, e.respuesta, e.source, e.fuente].join(" ").toLowerCase().includes(qq)));
  }, [items, estado, jur, doc, q]);

  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid={`${kind}-estado-chips`}>
        {Object.entries(ESTADO_FLUJO).map(([s, v]) => (
          <button key={s} type="button" onClick={() => setEstado(estado === s ? "todos" : s)} data-testid={`${kind}-chip-${s}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${estado === s ? "ring-2 ring-[#1E3A5F] " : ""}${v.cls}`}>
            {v.label} · {counts[s] || 0}
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" data-testid={`${kind}-search`}
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#CBD5E1] bg-white outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
        </div>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="h-10 w-44" data-testid={`${kind}-estado-filter`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {Object.entries(ESTADO_FLUJO).map(([s, v]) => <SelectItem key={s} value={s}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={jur} onValueChange={setJur}>
          <SelectTrigger className="h-10 w-48" data-testid={`${kind}-jur-filter`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las jurisdicciones</SelectItem>
            {meta.jurisdicciones.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
          </SelectContent>
        </Select>
        {kind === "why" && (
          <Select value={doc} onValueChange={setDoc}>
            <SelectTrigger className="h-10 w-56" data-testid="why-doc-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los contratos</SelectItem>
              {Object.entries(meta.documentos).map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button onClick={() => setEditing({})} className="h-10 bg-[#1E3A5F] hover:bg-[#16304F]" data-testid={`${kind}-new`}>
          <Plus className="w-4 h-4 mr-1" /> Nueva
        </Button>
      </div>
      <ul className="mt-5 rounded-2xl bg-white border border-[#E2DCD2] divide-y divide-[#E2DCD2]" data-testid={`${kind}-list`}>
        {loading && <li className="p-8 text-center text-slate-500">Cargando…</li>}
        {!loading && !filtered.length && <li className="p-8 text-center text-slate-500">Sin resultados con estos filtros.</li>}
        {filtered.map((e) => <EntryRow key={e.id} kind={kind} e={e} meta={meta} onEdit={setEditing} onDone={(d) => upsert(d, false)} />)}
      </ul>
      <EntryDialog kind={kind} meta={meta} entry={editing?.id ? editing : null} open={!!editing}
        onClose={() => setEditing(null)} onSaved={upsert} />
    </div>
  );
};
